/**
 * Phase 8 · Cluster C-44 — Discord sourcing connector.
 *
 * Spec:  docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md Addendum F §33.7
 * Smoke: docs/ux/PHASE8_VISUAL_TRACKER.md §8.8
 *
 * Fetches members of a single Discord guild via the Discord REST API
 *   GET https://discord.com/api/v10/guilds/:id/members?limit=1000
 * then filters in-memory on the caller's SourcingQuery. The guild id
 * lives on `config_json.guildId`; the bot token lives on
 * `encrypted_secret` and is NEVER read by the browser — that's the
 * whole point of routing this connector through the Edge Function
 * proxy only (Discord does not serve CORS for authenticated bot calls
 * and would block a direct browser fetch anyway).
 *
 * Read-only. `auth_kind='bot_token'`. Rate limit: 50 requests / 10 s,
 * matching Discord's global bot limit. An in-memory token bucket per
 * org keeps a runaway agent loop from wasting the shared budget.
 */
import { supabase } from '@core/supabase/client';
import { sourceConnectorsService } from '@core/supabase/services/sourceConnectorsService';
import {
  clampLimit,
  ConnectorNotConfiguredError,
  ConnectorTransportError,
  type ConnectorHealth,
  type SourcingConnector,
  type SourcingQuery,
  type SourcingResult,
} from './types';

const DISCORD_LIMIT = 50;
const DISCORD_WINDOW_MS = 10 * 1000;
const PROXY_FN = 'ggj_b2b_sourcing_proxy';

interface BucketState {
  timestamps: number[];
}
const rateBuckets = new Map<string, BucketState>();

function acquireToken(orgId: string): boolean {
  const key = `discord:${orgId}`;
  const now = Date.now();
  const state = rateBuckets.get(key) ?? { timestamps: [] };
  state.timestamps = state.timestamps.filter((t) => now - t < DISCORD_WINDOW_MS);
  if (state.timestamps.length >= DISCORD_LIMIT) {
    rateBuckets.set(key, state);
    return false;
  }
  state.timestamps.push(now);
  rateBuckets.set(key, state);
  return true;
}

type DiscordPresence = 'online' | 'idle' | 'dnd' | 'offline';

interface DiscordMember {
  /** Discord user id, always a numeric string. */
  userId: string;
  username: string;
  nick?: string | null;
  avatarUrl?: string | null;
  joinedAt?: string | null;
  roles?: string[] | null;
  presence?: DiscordPresence;
  activity?: string | null;
}

interface ProxyResponse {
  members?: DiscordMember[];
}

/**
 * All Discord searches go through the Edge Function proxy. We never
 * attempt a direct browser call because Discord's bot API returns
 * `Access-Control-Allow-Origin: null` on authed requests and blocks the
 * fetch outright. When the proxy isn't deployed we raise a clean
 * ConnectorTransportError — the runner emits an ok:false tool_result
 * and the audit row records the failure.
 */
async function proxyFetchMembers(
  orgId: string,
  guildId: string,
  limit: number,
): Promise<DiscordMember[]> {
  try {
    const { data, error } = await supabase.functions.invoke(PROXY_FN, {
      body: {
        action: 'discord.guild_members',
        orgId,
        query: { guildId, limit: Math.min(limit, 1000) },
      },
    });
    if (error) throw error;
    const payload = (data ?? { members: [] }) as ProxyResponse;
    return Array.isArray(payload.members) ? payload.members : [];
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'proxy unavailable';
    throw new ConnectorTransportError('discord', msg);
  }
}

function matchesQuery(member: DiscordMember, keywords: string[]): boolean {
  if (!keywords.length) return true;
  const hay = [
    member.username ?? '',
    member.nick ?? '',
    member.activity ?? '',
    ...(member.roles ?? []),
  ]
    .join(' ')
    .toLowerCase();
  return keywords.every((k) => hay.includes(k));
}

function memberToResult(
  member: DiscordMember,
  guildId: string,
): SourcingResult {
  const display = member.nick?.trim() || member.username || member.userId;
  const rolesList = Array.isArray(member.roles) ? member.roles : [];
  const headline =
    member.activity?.trim() ||
    (rolesList.length > 0 ? `Roles: ${rolesList.slice(0, 3).join(', ')}` : undefined);
  return {
    externalId: member.userId,
    name: display,
    headline,
    locationText: undefined,
    profileUrl: `https://discord.com/users/${member.userId}`,
    avatarUrl: member.avatarUrl ?? undefined,
    signals: {
      username: member.username,
      presence: member.presence ?? 'offline',
      roles: rolesList,
      joinedAt: member.joinedAt ?? null,
      guildId,
    },
    source: 'discord',
  };
}

export const discordConnector: SourcingConnector = {
  kind: 'discord',

  async search(query: SourcingQuery, orgId: string): Promise<SourcingResult[]> {
    if (!orgId) return [];

    const row = await sourceConnectorsService.get(orgId, 'discord');
    if (!row) throw new ConnectorNotConfiguredError('discord');
    if (row.status !== 'active') {
      throw new ConnectorNotConfiguredError('discord', `status=${row.status}`);
    }
    if (row.authKind !== 'bot_token') {
      throw new ConnectorNotConfiguredError(
        'discord',
        `unsupported auth_kind "${row.authKind}"`,
      );
    }

    const guildId =
      typeof row.configJson?.guildId === 'string' ? (row.configJson.guildId as string) : '';
    if (!guildId) {
      throw new ConnectorNotConfiguredError('discord', 'missing guildId in config');
    }

    if (!acquireToken(orgId)) {
      throw new ConnectorTransportError(
        'discord',
        `rate limit exceeded (${DISCORD_LIMIT}/10s)`,
      );
    }

    const limit = clampLimit(query.limit);
    const members = await proxyFetchMembers(orgId, guildId, Math.max(limit * 4, 200));

    const keywords = [
      ...(query.keywords ?? []),
      ...(query.skills ?? []),
    ]
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);

    const matched = members.filter((m) => matchesQuery(m, keywords));

    void sourceConnectorsService.markUsed(orgId, 'discord');

    return matched.slice(0, limit).map((m) => memberToResult(m, guildId));
  },

  async healthcheck(orgId: string): Promise<ConnectorHealth> {
    const row = await sourceConnectorsService.get(orgId, 'discord');
    if (!row) return { ok: false, reason: 'not configured' };
    if (row.status !== 'active') return { ok: false, reason: row.status };
    const guildId =
      typeof row.configJson?.guildId === 'string' ? (row.configJson.guildId as string) : '';
    if (!guildId) return { ok: false, reason: 'missing guildId' };
    return { ok: true };
  },
};
